import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from 'Components/UI/PageHeader/PageHeader';
import BreadCrumb, { IBreadcrumbItem } from 'Components/UI/BreadCrumb';
import Table from 'Components/UI/Table';
import SignalR from 'Components/UI/SignalR';
import { tableColumns } from './Notification.columns';
import { NotificationActions } from 'Store/Notification/Notification.actions';
import TableActionTopbar, { ITableActionTopbarButton } from 'Components/UI/TableActionTopbar';
import { INotificationData } from 'Data/interfaces/Notification/INotificationResponse';
import toastHandler from 'Utils/toastHandler';
import { INotificationsMapAux } from 'Data/interfaces/Notification/INotificationsMapAux';
import LoadingNotification from 'Components/UI/CustomLoading/LoadingNotification';
import { getCurrentTenant } from 'Store/Tenant/Tenant.selector';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import {
  IFilterDateOption,
  IFilterObraOption,
  INotificationFilters,
  INotificationOrder,
  INotificationOrderKey,
  ISelectedObraFilter
} from 'Store/Notification/interfaces/INotificationState';
import { isObjectEmpty } from 'Utils/ObjectUtils';
import useWindowSize from 'Hooks/useWindowSize';
import dayjs from 'dayjs';
import { IUserInforResponse } from 'Data/interfaces/Auth/IUserInfoResponse';
import styles from './Notification.module.scss';
import { SwitchStyled } from './styles';

interface INotification {
  filters: INotificationFilters;
  notifications: INotificationData[];
  isLoadingListAll: boolean;
  userInfo?: IUserInforResponse;
  obrasList?: IObraResponse[];
  filtersDates: IFilterDateOption[];
  subtractDays: number;
  csIdsFilterObra: number[];
  isShellCollapsed: boolean;
  onToggle: () => void;
}

const Notification: FC<INotification> = ({
  filters,
  notifications,
  isLoadingListAll,
  userInfo,
  obrasList,
  filtersDates,
  subtractDays,
  csIdsFilterObra,
  isShellCollapsed,
  onToggle
}) => {
  const dispatch = useDispatch();

  const currentTenant = useSelector(getCurrentTenant);
  const { height, width } = useWindowSize();
  const leftBarAndPaddingWidth = isShellCollapsed ? 132 : 307;

  const breadCrumb: IBreadcrumbItem[] = [
    {
      description: 'Central de notificações',
      href: '/notification',
    }
  ];

  const [selectedRows, setSelectedRow] = useState<INotificationData[]>([]);
  const [selectedObraFilter, setSelectedObraFilter] = useState<ISelectedObraFilter>({
    count: 0,
    clear: false
  });
  const [order, setOrder] = useState<INotificationOrder>({
    notifications: null,
    users: null,
    data: null,
    obra: null,
    last: null
  });
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const obrasMap = useMemo(() => {
    const obj: Record<number, IObraResponse> = {};
    if (obrasList) {
      for (const obra of obrasList) {
        if (
          notifications.some(x => x.ConstructionSiteFk === obra.ConstructionSiteId) &&
          !obj.hasOwnProperty(obra.ConstructionSiteId)
        ) {
          obj[obra.ConstructionSiteId] = obra;
        }
      }
    }
    return obj;
  }, [obrasList, notifications]);
  const obrasFilters = useMemo(() => {
    const array: IFilterObraOption[] = [];
    if (!isObjectEmpty(obrasMap)) {
      for (const obra of Object.values(obrasMap)) {
        array.push({
          id: obra.ConstructionSiteId,
          name: obra.Name,
          thumb: obra.ThumbnailImageUrl,
          image: obra.OriginalImageUrl
        });
      }
    }
    return array;
  }, [obrasMap]);

  const onChange = (selecteds: any, selectedRows: any) => {
    setSelectedRow(selectedRows);
  };

  const rowSelection = {
    onChange,
    columnWidth: 56,
  };

  const handleOrder = (key: INotificationOrderKey) => {
    if (!key) return;

    setOrder(prev => {
      const newState = {
        notifications: null,
        users: null,
        data: null,
        obra: null,
        last: null
      };
      if (prev[key] === null) {
        return {
          ...newState,
          [key]: 'ascend',
          last: key
        }
      }
      if (prev[key] === 'ascend') {
        return {
          ...newState,
          [key]: 'descend',
          last: key
        }
      }
      return {
        ...newState,
        [key]: null,
        last: null
      }
    });
  }

  const sortedNotifications = useMemo(() => {
    let filteredNotifications = notifications;

    if (showOnlyUnread) {
      filteredNotifications = filteredNotifications.filter(x => !x.hasRead);
    }

    if (!!order.last) {
      if (order.last === 'notifications') {
        if (order.notifications === 'ascend') {
          return filteredNotifications?.sort((a: INotificationData, b: INotificationData) => {
            return a.ActionName.localeCompare(b.ActionName);
          });
        }
        return filteredNotifications?.sort((a: INotificationData, b: INotificationData) => {
          return b.ActionName.localeCompare(a.ActionName);
        });
      }
      if (order.last === 'users') {
        if (order.users === 'ascend') {
          return filteredNotifications?.sort((a: INotificationData, b: INotificationData) => {
            return a.UserName.localeCompare(b.UserName);
          });
        }
        return filteredNotifications?.sort((a: INotificationData, b: INotificationData) => {
          return b.UserName.localeCompare(a.UserName);
        });
      }
      if (order.last === 'obra') {
        if (order.obra === 'ascend') {
          return filteredNotifications?.sort((a: INotificationData, b: INotificationData) => {
            return obrasMap[a.ConstructionSiteFk].Name.localeCompare(obrasMap[b.ConstructionSiteFk].Name);
          });
        }
        return filteredNotifications?.sort((a: INotificationData, b: INotificationData) => {
          return obrasMap[b.ConstructionSiteFk].Name.localeCompare(obrasMap[a.ConstructionSiteFk].Name);
        });
      }
      if (order.last === 'data') {
        if (order.data === 'ascend') {
          return filteredNotifications?.sort((a: INotificationData, b: INotificationData) => {
            return dayjs(b.DateCreateUtc).isBefore(dayjs(a.DateCreateUtc)) ? 1 : -1;
          });
        }
        return filteredNotifications?.sort((a: INotificationData, b: INotificationData) => {
          return dayjs(a.DateCreateUtc).isBefore(dayjs(b.DateCreateUtc)) ? 1 : -1;
        });
      }
    }

    return filteredNotifications?.sort((a: INotificationData, b: INotificationData) => {
      return b.NotificationId - a.NotificationId;
    });
  }, [notifications, order, obrasMap, showOnlyUnread]);

  const setSelectedAsRead = () => {
    let notificationsMap: INotificationsMapAux[] = selectedRows.map(
      (notification) => {
        return {
          NotificationId: !notification.hasRead ? notification.NotificationId : 0,
          UserFk: notification.UserFk,
          userInfoTracking: userInfo,
          currentTenantTracking: currentTenant,
        } as INotificationsMapAux;
      }
    );

    notificationsMap = notificationsMap.filter(x => x.NotificationId > 0);

    if (notificationsMap?.length > 0) {
      dispatch(NotificationActions.setNotificationAsView(notificationsMap));
      dispatch(NotificationActions.setNotificationAsRead(notificationsMap.map(x => x?.NotificationId ?? 0)));

      setTimeout(() => {
        toastHandler.showSuccess('Notificações marcadas como lidas!')
      }, 500);
    }
  }

  const setAllAsRead = () => {
    dispatch(NotificationActions.setReadNotification({ userInfo }));

    setTimeout(() => {
      toastHandler.showSuccess('Notificações marcadas como lidas!')
    }, 500);
  }

  const buttonsTableToolbar: ITableActionTopbarButton[] = [
    {
      description: 'Mostrar apenas não lidas',
      iconComponent: (
        <SwitchStyled
          checked={showOnlyUnread}
          size="small"
          onChange={() => setShowOnlyUnread(prev => !prev)}
        />
      ),
    },
    {
      icon: 'confirmar',
      description: selectedRows.length > 0 ? 'Marcar selecionadas como lidas' : 'Marcar todas como lidas',
      iconCustomSize: 10,
      action: selectedRows.length > 0 ? setSelectedAsRead : setAllAsRead,
    },
    {
      icon: 'openNewTab',
      description: 'Voltar para notificações novas',
      action: onToggle,
    }
  ];

  useEffect(() => {
    dispatch(NotificationActions.listNotification());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (csIdsFilterObra.length > 0 && !isLoadingListAll) {
      dispatch(NotificationActions.setShowFilterObra(true));
    }
  }, [csIdsFilterObra, isLoadingListAll, dispatch])

  return (
    <>
      <SignalR />
      <PageHeader name="Notificações">
        <BreadCrumb breadcrumbs={breadCrumb} />
        <div className={styles['space']} />
        <div className={styles['WrapperTable']}>
          {isLoadingListAll ?
            <LoadingNotification multiple={13} /> :
            <div className={styles['tableContainer']}>
              <TableActionTopbar buttons={buttonsTableToolbar} />
              <Table
                virtual
                scroll={{
                  x: width - leftBarAndPaddingWidth,
                  y: height - 331
                }}
                rowKey={(record: INotificationData) => record.NotificationId}
                columns={tableColumns({
                  filters,
                  notificationList: [...sortedNotifications],
                  dates: filtersDates,
                  subtractDays,
                  csIdsFilterObra,
                  obrasMap,
                  obrasFilters,
                  selectedObraFilter,
                  order,
                  setSelectedObraFilter,
                  onOrder: handleOrder,
                  dispatch,
                })}
                dataSource={[...sortedNotifications]}
                className={styles['listTable']}
                rowClassName={styles['fileListRow']}
                rowSelection={rowSelection}
              />
            </div>
          }
        </div>
      </PageHeader>
    </>
  );
}

export default Notification;
